/* eslint-disable react-hooks/exhaustive-deps */
import {
  DatCtaButton,
  DatTable,
  DatWait,
  DatSelectField,
  DatSubTitle,
  DatGraphBars,
} from 'components/DebtAssessment'
import { useEffect, useReducer } from 'react'
import { DatGraph } from '@achieve/cx-debt-assessment-graph'
import {
  datWaitPropsBase,
  extractGraphProps,
  extractTableProps,
  extractWaitProps,
  graphPropsBase,
  monthsToYears,
  tablePropsBase,
} from 'constants/DebtAssessment'
import { Typography } from 'components/Contentful'
import logger from '@achieve/cx-logger'

const ReducerActions = {
  SET_SELECTED_VALUE: 'SET_SELECTED_VALUE',
  SET_API_RESPONSE: 'SET_API_RESPONSE',
}

const reducer = (state, action) => {
  switch (action.type) {
    case ReducerActions.SET_SELECTED_VALUE:
      return { ...state, selectedValue: action.payload }
    case ReducerActions.SET_API_RESPONSE:
      return { ...state, apiResponse: action.payload }
    default:
      return state
  }
}

export const DatSection = ({
  subtitle = 'Here’s your estimated debt summary based on your credit card balance:',
  onCtaClick,
  onClick,
  onResponse,
  CtaCopy = 'See your options',
  CtaUrl = '/',
  selectItemList = [],
  defaultSelectedValue = '',
  tableProps = tablePropsBase,
  datWaitProps = datWaitPropsBase,
  graphProps = graphPropsBase,
  fireEvent = () => {},
  productType = 'AR',
  apiUrl = 'http://localhost:8080/api/achieve-tools/debt-assessment',
  variation = 'line',
  CtaDisclaimer = null,
  productTypeTerms = 0,
}) => {
  const initialValues = {
    selectedValue: defaultSelectedValue || selectItemList?.[0]?.value,
    apiResponse: {},
  }
  const [{ selectedValue, apiResponse }, dispatch] = useReducer(reducer, initialValues)

  const apiCall = async () => {
    try {
      const data = {
        estimated_debt: selectedValue,
      }

      const JSONdata = JSON.stringify(data)
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSONdata,
      }

      const response = await fetch(apiUrl, options)
      const result = await response.json()
      dispatch({ type: ReducerActions.SET_API_RESPONSE, payload: result })
      if (typeof onResponse === 'function') {
        onResponse(result)
      }
    } catch (error) {
      logger.error('Debt assessment API call failed', { error })
    }
  }

  useEffect(() => {
    apiCall()
  }, [selectedValue])

  const mapWaitProps = () => {
    return Object.keys(apiResponse).length !== 0
      ? { ...datWaitProps, variation, ...extractWaitProps(apiResponse) }
      : { ...datWaitProps, variation }
  }

  const mapTableProps = () => {
    let idxOffer, offersAvailable

    switch (productType) {
      case 'AR':
        offersAvailable = apiResponse?.default_offers?.ar_offers
        break
      case 'PL':
        offersAvailable = apiResponse?.default_offers?.apl_offers
        break
      case 'HL':
        offersAvailable = apiResponse?.default_offers?.ahl_offers
        break
    }

    /**
     * Determines which offer to use from the available offers array.
     *
     * If productTypeTerms is provided from config on contentful:
     * - Searches for an offer with matching terms
     * - Uses the found offer's index, or -1 if not found
     *
     * If productTypeTerms is not provided:
     * - Filters offers by defaultOffer terms_category if any matches exist (example offer1, offer2...)
     * - Defaults to using the first offer (index 0)
     *
     * Finally, selects the specific offer object at the determined index.
     */
    if (productTypeTerms) {
      const idxFound = offersAvailable?.map((i) => i?.terms).indexOf(productTypeTerms) || -1
      if (idxFound >= 0) idxOffer = idxFound
    } else {
      if (offersAvailable?.filter((i) => i.terms_category == 'offer2')?.length) {
        offersAvailable = offersAvailable?.filter((i) => i.terms_category == 'offer2')
      }
      idxOffer = 0
    }
    offersAvailable = offersAvailable?.[idxOffer]

    return Object.keys(apiResponse).length !== 0
      ? {
          ...tableProps,
          achieveTime: monthsToYears(Math.round(offersAvailable?.terms || 0)),
          variation,
          ...extractTableProps(apiResponse, productType),
        }
      : { ...tableProps, variation }
  }

  const mapGraphProps = () => {
    return Object.keys(apiResponse).length !== 0
      ? { ...graphProps, ...extractGraphProps(apiResponse), selectedValue }
      : { ...graphProps }
  }

  return (
    <>
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-11 lg:gap-8">
        <div className="lg:col-span-4 flex flex-col gap-6">
          <div>
            <div className="pb-4">
              <DatSubTitle title={subtitle} />
            </div>
            <DatSelectField
              selectItemList={selectItemList}
              defaultSelectedValue={defaultSelectedValue}
              onChange={(e) => {
                dispatch({
                  type: ReducerActions.SET_SELECTED_VALUE,
                  payload: e.target.value,
                })
                fireEvent(e.target.value)
              }}
            />
          </div>
          <div className="hidden lg:block">
            <DatTable {...mapTableProps()} />
          </div>
          <div className="hidden lg:block">
            {' '}
            <DatCtaButton onCtaClick={onCtaClick} copy={CtaCopy} url={CtaUrl} />
            {CtaDisclaimer && (
              <Typography
                variant="bodyXs"
                className="mt-4 text-[#5C708A]"
                content={CtaDisclaimer}
              />
            )}
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col ">
          {variation == 'line' ? (
            <div>
              <DatGraph {...mapGraphProps()} />
            </div>
          ) : (
            <div>
              <DatGraphBars {...mapGraphProps()} />
            </div>
          )}
          <div className="lg:hidden">
            <div className="my-6">
              <DatTable {...mapTableProps()} />
            </div>{' '}
            {variation == 'line' ? (
              <>
                <DatCtaButton onCtaClick={onCtaClick} copy={CtaCopy} url={CtaUrl} />
                {CtaDisclaimer && (
                  <Typography
                    variant="bodyXs"
                    fontWeight="medium"
                    className="mt-4 text-[#5C708A]"
                    content={CtaDisclaimer}
                  />
                )}
              </>
            ) : (
              <>
                <div className="lg:hidden">
                  <DatWait onClick={onClick} {...mapWaitProps()} />
                </div>
                <div className="my-5">
                  <DatCtaButton onCtaClick={onCtaClick} copy={CtaCopy} url={CtaUrl} />
                  {CtaDisclaimer && (
                    <Typography
                      variant="bodyXs"
                      fontWeight="medium"
                      className="mt-4 text-[#5C708A]"
                      content={CtaDisclaimer}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <div className="hidden lg:block">
            <DatWait onClick={onClick} {...mapWaitProps()} />
          </div>
        </div>
      </div>
    </>
  )
}
